import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useAuth } from '../contexts/AuthContext';
import { useSocket } from '../contexts/SocketContext';
import api, { API_ENDPOINTS } from '../config/api';
import toast from 'react-hot-toast';
import { stripHtmlTags } from '../utils/htmlUtils';
import {
  ArrowLeft,
  User,
  Calendar,
  DollarSign,
  Clock,
  CheckCircle,
  XCircle,
  MessageCircle,
  Eye,
  Star,
  MapPin,
  Grid,
  Loader2
} from 'lucide-react';

const GigOrdersPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { socket } = useSocket();
  const queryClient = useQueryClient();
  const [selectedOrder, setSelectedOrder] = useState(null);

  // Fetch gig details
  const { data: gig, isLoading: gigLoading } = useQuery(
    ['gig', id],
    () => api.get(API_ENDPOINTS.GIGS.DETAIL(id)),
    {
      select: (data) => data.data,
      enabled: !!id,
    }
  );

  // Fetch gig orders with auto-refresh
  const { data: ordersData, isLoading: ordersLoading, refetch: refetchOrders } = useQuery(
    ['gigOrders', id],
    () => api.get(API_ENDPOINTS.GIGS.ORDERS(id)),
    {
      select: (data) => data.data,
      enabled: !!id,
      staleTime: 10000, // Consider data stale after 10 seconds
      refetchInterval: 15000, // Auto-refetch every 15 seconds
      refetchOnWindowFocus: true, // Refetch when window regains focus
      refetchOnMount: true, // Always refetch on mount
    }
  );

  const orders = ordersData?.orders || [];

  // Listen for real-time updates via socket
  useEffect(() => {
    if (!socket || !id) return;

    const handleGigUpdated = (data) => {
      if (data.gigId === id || data._id === id) {
        // Refetch orders when gig is updated
        refetchOrders();
      }
    };

    const handleOrderUpdated = (data) => {
      if (data.gigId === id) {
        // Refetch orders when an order is updated
        refetchOrders();
      }
    };

    socket.on('gig:updated', handleGigUpdated);
    socket.on('order:updated', handleOrderUpdated);
    socket.on('order:approved', handleOrderUpdated);
    socket.on('order:rejected', handleOrderUpdated);

    return () => {
      socket.off('gig:updated', handleGigUpdated);
      socket.off('order:updated', handleOrderUpdated);
      socket.off('order:approved', handleOrderUpdated);
      socket.off('order:rejected', handleOrderUpdated);
    };
  }, [socket, id, refetchOrders]);

  // Approve order mutation
  const approveOrderMutation = useMutation(
    (orderId) => api.post(API_ENDPOINTS.GIGS.APPROVE_ORDER(id, orderId)),
    {
      onSuccess: async (data) => {
        toast.success('Order approved successfully!');
        // Invalidate and refetch immediately
        await Promise.all([
          queryClient.invalidateQueries(['gigOrders', id]),
          queryClient.invalidateQueries(['gig', id])
        ]);
        // Refetch orders data immediately
        refetchOrders();
        setSelectedOrder(null);
        // Navigate to chat if chatId is provided
        if (data.data?.chat) {
          navigate(`/chats/${data.data.chat}`);
        }
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to approve order');
      }
    }
  );

  // Reject order mutation
  const rejectOrderMutation = useMutation(
    (orderId) => api.post(API_ENDPOINTS.GIGS.REJECT_ORDER(id, orderId)),
    {
      onSuccess: async () => {
        toast.success('Order rejected');
        // Invalidate and refetch immediately
        await Promise.all([
          queryClient.invalidateQueries(['gigOrders', id]),
          queryClient.invalidateQueries(['gig', id])
        ]);
        // Refetch orders data immediately
        refetchOrders();
        setSelectedOrder(null);
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to reject order');
      }
    }
  );

  const handleApprove = (orderId) => {
    approveOrderMutation.mutate(orderId);
  };

  const handleReject = (orderId) => {
    rejectOrderMutation.mutate(orderId);
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
      accepted: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      rejected: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
      'in-progress': 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
    };
    return colors[status] || 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
  };

  const formatPrice = (gig) => {
    if (!gig.pricing) return 'Price negotiable';
    
    // New structure: basic/premium are objects with price property
    if (gig.pricing.basic) {
      const basicPrice = typeof gig.pricing.basic === 'object' && gig.pricing.basic.price 
        ? gig.pricing.basic.price 
        : gig.pricing.basic;
      const premiumPrice = gig.pricing.premium && typeof gig.pricing.premium === 'object' && gig.pricing.premium.price
        ? gig.pricing.premium.price
        : (typeof gig.pricing.premium === 'number' ? gig.pricing.premium : null);
      
      if (premiumPrice) {
        return `$${Number(basicPrice).toFixed(2)} - $${Number(premiumPrice).toFixed(2)}`;
      }
      return `$${Number(basicPrice).toFixed(2)}`;
    }
    
    // Legacy structure: basic/standard/premium are numbers
    if (typeof gig.pricing.basic === 'number') return `$${gig.pricing.basic.toFixed(2)}`;
    if (typeof gig.pricing.standard === 'number') return `$${gig.pricing.standard.toFixed(2)}`;
    if (typeof gig.pricing.premium === 'number') return `$${gig.pricing.premium.toFixed(2)}`;

    // Min/max range pricing
    if (gig.pricing.min && gig.pricing.max) {
      return `$${gig.pricing.min.toFixed(2)} - $${gig.pricing.max.toFixed(2)}`;
    }
    if (gig.pricing.min) return `$${gig.pricing.min.toFixed(2)}+`;

    return 'Price negotiable';
  };

  // Use gig from ordersData if available (has status counts), otherwise use gig from detail query
  const displayGig = ordersData?.gig || gig;

  if (gigLoading || ordersLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-accent"></div>
      </div>
    );
  }

  if (!gig) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <div className="text-6xl mb-4">❌</div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
            Gig not found
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            The gig you're looking for doesn't exist or has been removed.
          </p>
          <button
            onClick={() => navigate('/gigs')}
            className="btn-primary"
          >
            Browse Gigs
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center space-x-4 mb-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors duration-200"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-3xl font-heading font-bold text-heading">
              Gig Orders
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Review and manage orders for "{displayGig?.title || gig?.title}"
            </p>
          </div>
        </div>

        {/* Gig Info */}
        <div className="card p-6 mb-6">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
                {displayGig?.title || gig?.title}
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-4 line-clamp-2">
                {stripHtmlTags(displayGig?.description || gig?.description)}
              </p>
              <div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-400">
                <div className="flex items-center space-x-1">
                  <DollarSign className="w-4 h-4" />
                  <span>{formatPrice(displayGig || gig)}</span>
                </div>
                {(displayGig || gig)?.deliveryTime && (displayGig || gig).deliveryTime.value && (displayGig || gig).deliveryTime.unit && (
                  <div className="flex items-center space-x-1">
                    <Clock className="w-4 h-4" />
                    <span>{(displayGig || gig).deliveryTime.value} {(displayGig || gig).deliveryTime.unit}</span>
                  </div>
                )}
                {(displayGig || gig)?.location && (
                  <div className="flex items-center space-x-1">
                    <MapPin className="w-4 h-4" />
                    <span>{(displayGig || gig).location.remote ? 'Remote' : (displayGig || gig).location.city || 'Location not specified'}</span>
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center space-x-2 flex-wrap gap-2">
              <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                (displayGig || gig)?.type === 'professional' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
              }`}>
                {((displayGig || gig)?.type || '').charAt(0).toUpperCase() + ((displayGig || gig)?.type || '').slice(1)}
              </span>
              {/* Show order status counts */}
              {(displayGig || gig)?.orderStatusCounts && (
                <>
                  {(displayGig || gig).orderStatusCounts.completed > 0 && (
                    <span className="px-3 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                      Completed ({(displayGig || gig).orderStatusCounts.completed})
                    </span>
                  )}
                  {(displayGig || gig).orderStatusCounts.archived > 0 && (
                    <span className="px-3 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200">
                      Archived ({(displayGig || gig).orderStatusCounts.archived})
                    </span>
                  )}
                  {(displayGig || gig).orderStatusCounts.pending > 0 && (
                    <span className="px-3 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
                      Pending ({(displayGig || gig).orderStatusCounts.pending})
                    </span>
                  )}
                  {(displayGig || gig).orderStatusCounts.accepted > 0 && (
                    <span className="px-3 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                      Accepted ({(displayGig || gig).orderStatusCounts.accepted})
                    </span>
                  )}
                  {(displayGig || gig).orderStatusCounts['in-progress'] > 0 && (
                    <span className="px-3 py-1 text-xs font-medium rounded-full bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200">
                      In Progress ({(displayGig || gig).orderStatusCounts['in-progress']})
                    </span>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Orders List */}
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Orders ({orders.length})
          </h3>
        </div>

        {orders.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">📦</div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
              No orders yet
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              No one has ordered this gig yet. Share the gig to get more orders.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {orders.map((order) => (
              <div key={order._id} className="card p-6">
                <div className="flex items-start justify-between mb-4">
                  <Link 
                    to={`/profile/${order.client?.username || order.client?._id || order.client}`}
                    className="flex items-center space-x-3 hover:opacity-80 transition-opacity"
                  >
                    {order.client?.profile?.avatar ? (
                      <img 
                        src={order.client.profile.avatar} 
                        alt={order.client.username}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center">
                        <User className="w-6 h-6 text-gray-500" />
                      </div>
                    )}
                    <div>
                      <h4 className="font-semibold text-gray-900 dark:text-gray-100">
                        {order.client?.username || 'Unknown User'}
                      </h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Ordered {new Date(order.orderedAt).toLocaleDateString()}
                      </p>
                    </div>
                  </Link>
                  <span className={`px-3 py-1 text-xs font-medium rounded-full ${getStatusColor(order.status)}`}>
                    {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                  </span>
                </div>

                <div className="space-y-3 mb-4">
                  <div>
                    <h5 className="font-medium text-gray-900 dark:text-gray-100 mb-1">
                      Project Requirements
                    </h5>
                    <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-3">
                      {order.requirements}
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <h5 className="font-medium text-gray-900 dark:text-gray-100 mb-1">
                        Budget
                      </h5>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        ${order.budget?.toFixed(2) || 'Not specified'}
                      </p>
                    </div>
                    <div>
                      <h5 className="font-medium text-gray-900 dark:text-gray-100 mb-1">
                        Timeline
                      </h5>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {order.timeline || 'Not specified'} days
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    {(order.status === 'accepted' || order.status === 'in-progress' || order.status === 'completed') && (
                      <Link
                        to={order.chatId ? `/chats/${order.chatId}` : "/chats"}
                        className="btn-primary text-sm flex items-center"
                      >
                        <MessageCircle className="w-4 h-4 mr-2" />
                        Open Chat
                      </Link>
                    )}
                  </div>

                  {order.status === 'pending' && (
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleReject(order._id)}
                        disabled={rejectOrderMutation.isLoading || approveOrderMutation.isLoading}
                        className="btn-outline text-red-600 hover:text-red-700 border-red-300 hover:border-red-400"
                      >
                        <XCircle className="w-4 h-4 mr-2" />
                        Reject
                      </button>
                      <button
                        onClick={() => handleApprove(order._id)}
                        disabled={approveOrderMutation.isLoading}
                        className="btn-primary"
                      >
                        {approveOrderMutation.isLoading ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Approving...
                          </>
                        ) : (
                          <>
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Approve
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default GigOrdersPage;
